import { useState, useEffect, useCallback, useMemo } from 'react';
import { AnimatePresence, motion } from 'motion/react';
import { API_BASE } from '../config';
import { track } from '../trackActivity';
import { formatDate, formatDateForFilename } from '../utils/formatDate';

function pad(n) { return String(n).padStart(2, '0'); }
function toISO(y, m, d) { return `${y}-${pad(m)}-${pad(d)}`; }
function todayISO() { const d = new Date(); return toISO(d.getFullYear(), d.getMonth() + 1, d.getDate()); }
function addDays(iso, days) {
    const d = new Date(iso + 'T00:00:00Z');
    d.setUTCDate(d.getUTCDate() + days);
    return toISO(d.getUTCFullYear(), d.getUTCMonth() + 1, d.getUTCDate());
}
function firstOfThisMonth() {
    const d = new Date();
    return toISO(d.getFullYear(), d.getMonth() + 1, 1);
}

// `status` = the statusOf() value a tab shows; 'issues' = needs action now.
const TABS = [
    { key: 'issues',          label: 'With issues' },
    { key: 'yet_to_dispatch', label: 'Yet to Dispatch', status: 'Yet to Dispatch' },
    { key: 'not_dispatched',  label: 'Not Dispatched',  status: 'Not Dispatched' },
    { key: 'short',           label: 'Short',           status: 'Short' },
    { key: 'excess',          label: 'Excess',          status: 'Excess' },
    { key: 'matched',         label: 'Matched',         status: 'Matched' },
    { key: 'cancelled',       label: 'Cancelled',       status: 'Cancelled' },
    { key: 'all',             label: 'All' },
];
const ISSUE_STATUSES = ['Not Dispatched', 'Short', 'Excess'];

// A PO normally takes up to 2 days to dispatch, so a PO with nothing
// invoiced is only "Not Dispatched" once it's MORE than 2 days old; until
// then it's "Yet to Dispatch" (not an issue, and not counted as loss).
const DISPATCH_GRACE_DAYS = 2;
function daysSince(dateValue) {
    if (!dateValue) return 0;
    const iso = String(dateValue).slice(0, 10);
    const [y, m, d] = iso.split('-').map(Number);
    const po = Date.UTC(y, m - 1, d);
    const t = new Date();
    const today = Date.UTC(t.getFullYear(), t.getMonth(), t.getDate());
    return Math.round((today - po) / 86400000);
}

function statusOf(r) {
    if (r.po_status === 'cancelled') return 'Cancelled';
    if (r.invoice_quantity === 0) return daysSince(r.po_date) > DISPATCH_GRACE_DAYS ? 'Not Dispatched' : 'Yet to Dispatch';
    if (r.difference > 0) return 'Short';
    if (r.difference < 0) return 'Excess';
    return 'Matched';
}

const STATUS_PILL = {
    'Yet to Dispatch': { bg: '#E3EEFB', fg: '#2563EB' },
    'Not Dispatched':  { bg: '#EAE6F3', fg: 'var(--svi-purple)' },
    'Short':           { bg: 'var(--svi-error-tint)', fg: 'var(--svi-error)' },
    'Excess':          { bg: '#F5EDDC', fg: '#9A7B3E' },
    'Matched':         { bg: 'var(--svi-green-tint)', fg: 'var(--svi-green)' },
    'Cancelled':       { bg: '#ECECEF', fg: '#5B5966' },
};

// Value at risk on a (PO, SKU) line: short / not-dispatched lines lose the
// shortfall; a cancelled PO's line loses its whole un-invoiced qty (both via
// so-vs-invoice.js's potential_loss = shortfall x SO rate). "Yet to
// Dispatch" is still in the pipeline, so it isn't counted as lost.
function lossOf(r) {
    const st = statusOf(r);
    return st === 'Short' || st === 'Not Dispatched' || st === 'Cancelled' ? Number(r.potential_loss || 0) : 0;
}

// PO-level status for the management report (po_overview rows).
function poStatusOf(p) {
    if (p.po_status === 'cancelled') return 'Cancelled';
    if ((p.inv_count || 0) > 0) return p.so_qty && Number(p.inv_qty || 0) < Number(p.so_qty) ? 'Short' : 'Dispatched';
    return daysSince(p.po_date) > DISPATCH_GRACE_DAYS ? 'Not Dispatched' : 'Yet to Dispatch';
}
const PO_STATUSES = [
    { key: 'Dispatched',      label: 'Fully dispatched', color: '#2F7A4B', tab: 'matched' },
    { key: 'Short',           label: 'Dispatched short', color: '#C2410C', tab: 'short' },
    { key: 'Not Dispatched',  label: `Not dispatched (${DISPATCH_GRACE_DAYS}+ days)`, color: '#6E58A6', tab: 'not_dispatched' },
    { key: 'Yet to Dispatch', label: `Yet to dispatch (≤${DISPATCH_GRACE_DAYS} days)`, color: '#2563EB', tab: 'yet_to_dispatch' },
    { key: 'Cancelled',       label: 'Cancelled', color: '#5B5966', tab: 'cancelled' },
];
const QUICK_RANGES = [
    { key: 'today', label: 'Today' },
    { key: '7d',    label: 'Last 7 days' },
    { key: '30d',   label: 'Last 30 days' },
    { key: 'month', label: 'This month' },
    { key: 'all',   label: 'All time' },
];

// SO vs Invoice — per-SKU comparison of what was put on a PO's Sales
// Order(s) against what was actually invoiced. Backend:
// backend/src/functions/so-vs-invoice.js. Only one network call for the
// detail list (scoped by date range only); PO-number search, SKU/party
// search, and the Short/Excess/Matched/With-issues/All tabs are all
// computed client-side from that one dataset, so tab counts update
// instantly with no extra round trips, and the Summary view's numbers
// reflect the same date-range scope regardless of what's typed into the
// detail table's own search boxes.
export default function SoVsInvoice({ user }) {
    const [fromDate, setFromDate] = useState(firstOfThisMonth());
    const [toDate, setToDate] = useState(todayISO());
    const [quickRange, setQuickRange] = useState('month');

    const [poSearch, setPoSearch] = useState('');
    const [textSearch, setTextSearch] = useState('');
    // SKU Master filters (sku_master.status / category, joined per EAN by
    // so-vs-invoice.js). Multi-select status; [] = all.
    const [skuStatuses, setSkuStatuses] = useState([]);
    const [skuCategory, setSkuCategory] = useState('');
    const [tab, setTab] = useState('issues');

    const [view, setView] = useState('detail'); // 'detail' | 'summary'
    const [rows, setRows] = useState([]);
    const [pos, setPos] = useState([]); // po_overview: every PO in range, incl. cancelled / no SO
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [toast, setToast] = useState('');

    const loadRows = useCallback(async () => {
        setLoading(true);
        setError(null);
        try {
            const qs = new URLSearchParams({ action: 'list' });
            if (fromDate) qs.set('date_from', fromDate);
            if (toDate) qs.set('date_to', toDate);
            const qsPo = new URLSearchParams(qs);
            qsPo.set('action', 'po_overview');
            const [res, resPo] = await Promise.all([
                fetch(`${API_BASE}/so-vs-invoice?${qs.toString()}`),
                fetch(`${API_BASE}/so-vs-invoice?${qsPo.toString()}`),
            ]);
            const data = await res.json();
            if (!res.ok || !data.success) throw new Error(data.error || 'Failed to load');
            setRows(data.rows);
            const dataPo = await resPo.json().catch(() => null);
            setPos(dataPo && dataPo.success ? dataPo.pos : []);
        } catch (err) {
            setError(err.message);
        }
        setLoading(false);
    }, [fromDate, toDate]);

    useEffect(() => { loadRows(); }, [loadRows]);

    function applyQuickRange(key) {
        setQuickRange(key);
        if (key === 'today') { setFromDate(todayISO()); setToDate(todayISO()); }
        else if (key === '7d') { setFromDate(addDays(todayISO(), -6)); setToDate(todayISO()); }
        else if (key === '30d') { setFromDate(addDays(todayISO(), -29)); setToDate(todayISO()); }
        else if (key === 'month') { setFromDate(firstOfThisMonth()); setToDate(todayISO()); }
        else { setFromDate(''); setToDate(''); }
    }
    function onManualDateChange(setter, value) {
        setter(value);
        setQuickRange(null);
    }

    // Text search (PO number) + text search (SKU/party), applied client-side.
    const searchFiltered = useMemo(() => {
        const po = poSearch.trim().toLowerCase();
        const text = textSearch.trim().toLowerCase();
        return rows.filter(r =>
            (!po || r.po_number.toLowerCase().includes(po)) &&
            (!text || r.sku_name.toLowerCase().includes(text) || (r.party_name || '').toLowerCase().includes(text)) &&
            (skuStatuses.length === 0 || skuStatuses.includes(r.sku_status || NOT_IN_MASTER)) &&
            (!skuCategory || ((r.sku_category || '').trim() || NOT_IN_MASTER) === skuCategory)
        );
    }, [rows, poSearch, textSearch, skuStatuses, skuCategory]);

    // Options + line counts come from the loaded date range, so only values
    // that actually occur are offered.
    const skuFilterOptions = useMemo(() => {
        const statuses = {};
        const categories = new Set();
        for (const r of rows) {
            const st = r.sku_status || NOT_IN_MASTER;
            statuses[st] = (statuses[st] || 0) + 1;
            categories.add((r.sku_category || '').trim() || NOT_IN_MASTER);
        }
        const order = ['Active', 'Festive', 'Discontinued', 'Packing Material', NOT_IN_MASTER];
        const rank = (x) => (order.indexOf(x) === -1 ? 50 : order.indexOf(x));
        return {
            statuses: Object.entries(statuses).sort((a, b) => rank(a[0]) - rank(b[0]) || a[0].localeCompare(b[0])),
            categories: [...categories].sort((a, b) => (a === NOT_IN_MASTER) - (b === NOT_IN_MASTER) || a.localeCompare(b)),
        };
    }, [rows]);
    const toggleSkuStatus = (st) => setSkuStatuses(prev => prev.includes(st) ? prev.filter(x => x !== st) : [...prev, st]);

    const counts = useMemo(() => {
        const c = { issues: 0, all: searchFiltered.length };
        TABS.forEach(t => { if (t.status) c[t.key] = 0; });
        const keyByStatus = Object.fromEntries(TABS.filter(t => t.status).map(t => [t.status, t.key]));
        for (const r of searchFiltered) {
            const st = statusOf(r);
            c[keyByStatus[st]]++;
            if (ISSUE_STATUSES.includes(st)) c.issues++;
        }
        return c;
    }, [searchFiltered]);

    const tabFiltered = useMemo(() => {
        if (tab === 'all') return searchFiltered;
        if (tab === 'issues') return searchFiltered.filter(r => ISSUE_STATUSES.includes(statusOf(r)));
        const t = TABS.find(x => x.key === tab);
        return t ? searchFiltered.filter(r => statusOf(r) === t.status) : searchFiltered;
    }, [searchFiltered, tab]);

    // SO-vs-Invoice counts for the selected date range (ignores search/tab,
    // same scope as `summary` below) — each row carries a comma-joined list
    // of SO voucher(s)/invoice number(s), since more than one can cover the
    // same (po_number, ean); dedupe across all rows to get the real count
    // of distinct SOs/invoices touching this date range, not SKU-line count.
    // SO-vs-Invoice counts for whatever is filtered (date range + PO# +
    // party/SKU search + SKU Master filters). Each row carries comma-joined
    // SO voucher(s)/invoice number(s), so dedupe across rows to count real
    // documents, not SKU lines. SOs on cancelled POs are shown separately and
    // not counted as "pending".
    const soInvoiceSummary = useMemo(() => {
        const soSet = new Set();
        const invSet = new Set();
        const cancelledSo = new Set();
        const invoicedSo = new Set();
        for (const r of searchFiltered) {
            const vouchers = (r.so_vouchers || '').split(',').map(v => v.trim()).filter(Boolean);
            vouchers.forEach(v => soSet.add(v));
            (r.invoice_numbers || '').split(',').map(v => v.trim()).filter(Boolean).forEach(v => invSet.add(v));
            if (r.po_status === 'cancelled') vouchers.forEach(v => cancelledSo.add(v));
            if (r.invoice_quantity > 0) vouchers.forEach(v => invoicedSo.add(v));
        }
        const soCount = soSet.size;
        const invCount = invSet.size;
        const cancelled = cancelledSo.size;
        const live = soCount - cancelled;
        const pending = [...soSet].filter(v => !cancelledSo.has(v) && !invoicedSo.has(v)).length;
        const pct = live > 0 ? Math.round(((live - pending) / live) * 100) : 0;
        return { soCount, invCount, pct, gap: pending, cancelled };
    }, [searchFiltered]);

    const rangeLabel = useMemo(() => {
        const qr = QUICK_RANGES.find(q => q.key === quickRange);
        if (qr) return qr.label;
        if (fromDate && toDate) return `${formatDate(fromDate)} – ${formatDate(toDate)}`;
        return 'Selected range';
    }, [quickRange, fromDate, toDate]);

    // Summary follows EVERY filter: date range (server-side) plus the PO
    // number, SKU/party search and SKU Master status/category (client-side) — the tab only picks
    // which detail rows show, so it doesn't apply here. Potential Loss is
    // short qty x SO rate, computed per (PO, EAN) row by so-vs-invoice.js.
    const summary = useMemo(() => {
        const src = searchFiltered;
        const total_pos = new Set(src.map(r => r.po_number)).size;
        const total_so_qty = src.reduce((s, r) => s + r.so_quantity, 0);
        const total_invoice_qty = src.reduce((s, r) => s + r.invoice_quantity, 0);
        const short_supplied = src.reduce((s, r) => s + r.difference, 0);
        const total_loss = src.reduce((s, r) => s + lossOf(r), 0);
        const cancel_loss = src.reduce((s, r) => s + (statusOf(r) === 'Cancelled' ? lossOf(r) : 0), 0);
        const short_loss = total_loss - cancel_loss;
        const byEan = {};
        const byParty = {};
        for (const r of src) {
            if (r.difference <= 0) continue;
            const loss = lossOf(r);
            if (loss <= 0) continue;
            if (!byEan[r.ean]) byEan[r.ean] = { ean: r.ean, sku_name: r.sku_name, item_id: r.item_id, pack_size: r.pack_size, mrp: r.mrp, short_supplied: 0, potential_loss: 0, parties: {} };
            const e = byEan[r.ean];
            e.short_supplied += r.difference;
            e.potential_loss += loss;
            const pk = r.party_name || 'Unknown';
            e.parties[pk] = (e.parties[pk] || 0) + loss;

            if (!byParty[pk]) byParty[pk] = { party_name: pk, total_so_qty: 0, total_invoice_qty: 0, short_supplied: 0, potential_loss: 0, pos: new Set() };
            byParty[pk].total_so_qty += r.so_quantity;
            byParty[pk].total_invoice_qty += r.invoice_quantity;
            byParty[pk].short_supplied += r.difference;
            byParty[pk].potential_loss += loss;
            byParty[pk].pos.add(r.po_number);
        }
        // Top 10 by potential loss; "Mostly at" = the party carrying the
        // largest share of that SKU's loss, so it's clear where to chase.
        const top_loss_skus = Object.values(byEan)
            .filter(e => e.potential_loss > 0)
            .sort((a, b) => b.potential_loss - a.potential_loss)
            .slice(0, 10)
            .map(e => {
                const [topParty, topLoss] = Object.entries(e.parties).sort((a, b) => b[1] - a[1])[0] || ['—', 0];
                return { ...e, top_party: topParty, top_party_share: e.potential_loss > 0 ? Math.round((topLoss / e.potential_loss) * 100) : 0 };
            });
        const top10_loss = top_loss_skus.reduce((s, e) => s + e.potential_loss, 0);
        const top_short_parties = Object.values(byParty)
            .map(p => ({ ...p, po_count: p.pos.size }))
            .sort((a, b) => b.potential_loss - a.potential_loss || b.short_supplied - a.short_supplied)
            .slice(0, 8);
        return { total_pos, total_so_qty, total_invoice_qty, short_supplied, total_loss, cancel_loss, short_loss, top_loss_skus, top10_loss, top_short_parties };
    }, [searchFiltered]);

    // PO-level report: every PO in range (po_overview), narrowed by the same
    // filters as the table. PO# applies directly; the party/SKU search box
    // matches the party name or any PO whose SKU lines match; SKU Master
    // status/category limit it to POs that have a matching SKU line.
    const poReport = useMemo(() => {
        const po = poSearch.trim().toLowerCase();
        const text = textSearch.trim().toLowerCase();
        const skuFilterOn = skuStatuses.length > 0 || !!skuCategory;
        const linePOs = new Set(searchFiltered.map(r => r.po_number));
        const list = pos.filter(p =>
            (!po || String(p.po_number).toLowerCase().includes(po)) &&
            (!text || (p.party_name || '').toLowerCase().includes(text) || linePOs.has(p.po_number)) &&
            (!skuFilterOn || linePOs.has(p.po_number))
        ).map(p => ({ ...p, report_status: poStatusOf(p), age: daysSince(p.po_date) }));

        const by = Object.fromEntries(PO_STATUSES.map(st => [st.key, []]));
        list.forEach(p => by[p.report_status].push(p));
        const value = (p) => Number(p.so_value || p.po_amount || 0);
        const qty = (p) => Number(p.so_qty || p.po_units || 0);
        const live = list.filter(p => p.report_status !== 'Cancelled');
        const liveSo = live.reduce((a, p) => a + Number(p.so_qty || 0), 0);
        const liveInv = live.reduce((a, p) => a + Math.min(Number(p.inv_qty || 0), Number(p.so_qty || p.inv_qty || 0)), 0);
        const parties = [...new Set(list.map(p => p.party_name || 'Unknown'))];

        const byParty = {};
        for (const p of list) {
            const k = p.party_name || 'Unknown';
            const e = byParty[k] ||= { party_name: k, total: 0, so: 0, inv: 0, counts: {} };
            e.total++;
            e.counts[p.report_status] = (e.counts[p.report_status] || 0) + 1;
            if (p.report_status !== 'Cancelled') { e.so += Number(p.so_qty || 0); e.inv += Math.min(Number(p.inv_qty || 0), Number(p.so_qty || p.inv_qty || 0)); }
        }
        const partyRows = Object.values(byParty)
            .map(e => ({ ...e, fill: e.so > 0 ? Math.round((e.inv / e.so) * 100) : null, issues: (e.counts['Not Dispatched'] || 0) + (e.counts['Short'] || 0) + (e.counts['Cancelled'] || 0) }))
            .sort((a, b) => b.issues - a.issues || b.total - a.total);

        return {
            list, by, parties, partyRows,
            total: list.length,
            totalQty: list.reduce((a, p) => a + qty(p), 0),
            totalValue: list.reduce((a, p) => a + value(p), 0),
            cancelledValue: by['Cancelled'].reduce((a, p) => a + value(p), 0),
            cancelledQty: by['Cancelled'].reduce((a, p) => a + qty(p), 0),
            noSo: list.filter(p => p.report_status !== 'Cancelled' && !(p.so_count > 0) && !(p.inv_count > 0)).length,
            fill: liveSo > 0 ? Math.round((liveInv / liveSo) * 100) : null,
            overdue: [...by['Not Dispatched']].sort((a, b) => b.age - a.age),
        };
    }, [pos, poSearch, textSearch, skuStatuses, skuCategory, searchFiltered]);

    const scopeLabel = poReport.parties.length === 1
        ? poReport.parties[0]
        : textSearch.trim() ? `“${textSearch.trim()}”` : poSearch.trim() ? `PO “${poSearch.trim()}”` : 'All parties';

    async function openPdf(kind, id) {
        try {
            const url = kind === 'so'
                ? `${API_BASE}/acc-so-list?view=so_pdf&voucher_no=${encodeURIComponent(id)}`
                : `${API_BASE}/acc-invoices?action=invoice_pdf&invoice_no=${encodeURIComponent(id)}`;
            const res = await fetch(url);
            const data = await res.json();
            if (res.ok && data.success && data.pdf_url) window.open(data.pdf_url, '_blank', 'noopener');
            else showToast(`No PDF available for ${kind === 'so' ? 'SO' : 'invoice'} ${id}.`);
        } catch {
            showToast('Failed to load PDF.');
        }
    }
    function showToast(msg) {
        setToast(msg);
        setTimeout(() => setToast(''), 3000);
    }

    function downloadExcel() {
        if (!window.XLSX || tabFiltered.length === 0) return;
        const data = tabFiltered.map(r => ({
            'PO Number': r.po_number,
            'Party Name': r.party_name,
            'PO Date': formatDate(r.po_date),
            'SKU Name': r.sku_name,
            'SO Qty': r.so_quantity,
            'Invoice Qty': r.invoice_quantity,
            'Difference': r.difference,
        }));
        const ws = window.XLSX.utils.json_to_sheet(data);
        ws['!cols'] = [{ wch: 18 }, { wch: 28 }, { wch: 14 }, { wch: 40 }, { wch: 10 }, { wch: 12 }, { wch: 12 }]; // Excel format unchanged per explicit instruction
        const wb = window.XLSX.utils.book_new();
        window.XLSX.utils.book_append_sheet(wb, ws, 'SO vs Invoice');
        window.XLSX.writeFile(wb, `SO_vs_Invoice_${formatDateForFilename()}.xlsx`);
        track(user?.username, 'EXPORT', 'SO vs Invoice', { rows: tabFiltered.length });
    }

    return (
        <div style={S.page}>
            <style>{EMBEDDED_CSS}</style>

            <div style={S.headerCard}>
                <div>
                    <div style={S.kicker}>Invoicing</div>
                    <h1 style={S.title}>SO vs <b>Invoice</b></h1>
                    <p style={S.subtitle}>Per SKU: what was ordered on the Sales Order vs what was actually invoiced.</p>
                </div>
                <div style={{ display: 'flex', gap: 10 }}>
                    <button className="svi-tab" style={S.summaryBtn} onClick={() => setView(v => v === 'detail' ? 'summary' : 'detail')}>
                        {view === 'detail' ? '☰ Summary' : '← Back to Report'}
                    </button>
                    {view === 'detail' && (
                        <button className="svi-tab" style={S.downloadBtn} onClick={downloadExcel} disabled={tabFiltered.length === 0}>
                            ⬇ Download Excel
                        </button>
                    )}
                </div>
            </div>

            {toast && <div style={S.toast}>{toast}</div>}

            {view === 'detail' ? (
                <>
                    <div style={S.controlsCard}>
                        <div style={S.tabRow}>
                            {TABS.map(t => (
                                <button key={t.key} className="svi-tab" onClick={() => setTab(t.key)}
                                    style={{ ...S.tabBtn, ...(tab === t.key ? S.tabBtnActive : {}) }}>
                                    {t.label} <span style={{ ...S.tabCount, ...(tab === t.key ? S.tabCountActive : {}) }}>{counts[t.key]}</span>
                                </button>
                            ))}
                            <div style={{ flex: 1 }} />
                            {QUICK_RANGES.map(q => (
                                <button key={q.key} className="svi-tab"
                                    style={{ ...S.quickBtn, ...(quickRange === q.key ? S.quickBtnActive : {}) }}
                                    onClick={() => applyQuickRange(q.key)}>
                                    {q.label}
                                </button>
                            ))}
                        </div>

                        <div style={S.searchRow}>
                            <input className="svi-input" placeholder="PO# Search PO number" value={poSearch} onChange={e => setPoSearch(e.target.value)} style={{ width: 220 }} />
                            <input className="svi-input" placeholder="🔍 Search SKU or party name" value={textSearch} onChange={e => setTextSearch(e.target.value)} style={{ width: 290 }} />
                            <input className="svi-input" type="date" value={fromDate} onChange={e => onManualDateChange(setFromDate, e.target.value)} />
                            <span style={{ color: 'var(--svi-text-faint)', fontSize: 13 }}>to</span>
                            <input className="svi-input" type="date" value={toDate} onChange={e => onManualDateChange(setToDate, e.target.value)} />
                        </div>

                        <div style={S.skuFilterRow}>
                            <span style={S.skuFilterLabel}>SKU Master</span>
                            <button className="svi-tab" onClick={() => setSkuStatuses([])}
                                style={{ ...S.skuChip, ...(skuStatuses.length === 0 ? S.skuChipActive : {}) }}>
                                All statuses
                            </button>
                            {skuFilterOptions.statuses.map(([st, n]) => {
                                const on = skuStatuses.includes(st);
                                const c = SKU_STATUS_COLORS[st] || '#6E58A6';
                                return (
                                    <button key={st} className="svi-tab" onClick={() => toggleSkuStatus(st)} aria-pressed={on}
                                        style={{ ...S.skuChip, ...(on ? { background: c, borderColor: c, color: '#fff' } : { color: c, borderColor: `${c}55` }) }}>
                                        {on ? '✓ ' : ''}{st} <span style={{ opacity: 0.75, fontWeight: 600 }}>{n}</span>
                                    </button>
                                );
                            })}
                            <select className="svi-input" value={skuCategory} onChange={e => setSkuCategory(e.target.value)} style={{ marginLeft: 'auto', minWidth: 180 }}>
                                <option value="">All categories</option>
                                {skuFilterOptions.categories.map(c => <option key={c} value={c}>{c}</option>)}
                            </select>
                            {(skuStatuses.length > 0 || skuCategory) && (
                                <button className="svi-tab" style={S.skuClear} onClick={() => { setSkuStatuses([]); setSkuCategory(''); }}>Clear</button>
                            )}
                        </div>

                        <p style={S.showingText}>Showing <b>{tabFiltered.length}</b> of {searchFiltered.length} SKU lines</p>
                    </div>

                    <div style={S.soInvCard}>
                        <div style={S.soInvHeader}>
                            <span style={S.soInvTitle}>Sales Orders vs Invoices <span style={{ fontWeight: 500, color: 'var(--svi-text-faint)' }}>· {scopeLabel}</span></span>
                            <span style={S.soInvRange}>{rangeLabel}</span>
                        </div>
                        <div style={S.soInvBody}>
                            <div style={S.soInvStat}>
                                <div style={S.soInvNum}>{soInvoiceSummary.soCount}</div>
                                <div style={S.soInvLabel}>Sales Orders Made</div>
                            </div>
                            <div style={S.soInvVs}>vs</div>
                            <div style={S.soInvStat}>
                                <div style={{ ...S.soInvNum, color: 'var(--svi-purple)' }}>{soInvoiceSummary.invCount}</div>
                                <div style={S.soInvLabel}>Invoices Raised</div>
                            </div>
                            <div style={S.soInvDivider} />
                            <div style={S.soInvStat}>
                                <div style={{ ...S.soInvNum, fontSize: 22, color: soInvoiceSummary.gap > 0 ? 'var(--svi-error)' : 'var(--svi-green)' }}>
                                    {soInvoiceSummary.gap > 0 ? `${soInvoiceSummary.gap} pending` : 'All invoiced'}
                                </div>
                                <div style={S.soInvLabel}>SOs Not Yet Invoiced</div>
                            </div>
                            {soInvoiceSummary.cancelled > 0 && (
                                <div style={S.soInvStat}>
                                    <div style={{ ...S.soInvNum, fontSize: 22, color: '#5B5966' }}>{soInvoiceSummary.cancelled} cancelled</div>
                                    <div style={S.soInvLabel}>SOs on cancelled POs</div>
                                </div>
                            )}
                            <div style={S.soInvBarWrap}>
                                <div style={S.soInvBarTrack}>
                                    <div style={{ ...S.soInvBarFill, width: `${Math.min(soInvoiceSummary.pct, 100)}%` }} />
                                </div>
                                <span style={S.soInvPctText}>{soInvoiceSummary.pct}% invoiced</span>
                            </div>
                        </div>
                    </div>

                    <AnimatePresence mode="wait">
                        {loading ? (
                            <motion.p key="loading" style={S.emptyState} initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}>Loading…</motion.p>
                        ) : error ? (
                            <p style={{ ...S.emptyState, color: 'var(--svi-error)' }}>Failed to load: {error}</p>
                        ) : tabFiltered.length === 0 ? (
                            <p style={S.emptyState}>No matching rows.</p>
                        ) : (
                            <motion.div key="table" initial={{ opacity: 0, y: 8 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0 }} transition={{ duration: 0.18 }} style={S.card}>
                                <div style={S.scrollWrap}>
                                    <div style={{ minWidth: 1280 }}>
                                        <div style={{ ...S.gridRow, ...S.gridHeader, gridTemplateColumns: COLS }}>
                                            <div style={S.headCell}>#</div>
                                            <div style={S.headCell}>PO Number</div>
                                            <div style={S.headCell}>Invoice Number</div>
                                            <div style={S.headCell}>Party</div>
                                            <div style={S.headCell}>PO Date</div>
                                            <div style={S.headCell}>SKU</div>
                                            <div style={S.headCell}>Ageing</div>
                                            <div style={S.headCell}>SO Qty</div>
                                            <div style={S.headCell}>Invoice Qty</div>
                                            <div style={S.headCell}>Fill %</div>
                                            <div style={S.headCell}>Difference</div>
                                            <div style={S.headCell}>Status</div>
                                        </div>
                                        {tabFiltered.map((r, idx) => {
                                            const vouchers = (r.so_vouchers || '').split(',').filter(Boolean);
                                            const invoices = (r.invoice_numbers || '').split(',').filter(Boolean);
                                            const fillPct = r.so_quantity > 0 ? Math.round((r.invoice_quantity / r.so_quantity) * 100) : 0;
                                            const status = statusOf(r);
                                            return (
                                                <div key={`${r.po_number}-${r.ean}-${idx}`} className="svi-row" style={{ ...S.gridRow, gridTemplateColumns: COLS }}>
                                                    <div style={{ ...S.cell, color: 'var(--svi-text-faint)' }}>{idx + 1}</div>
                                                    <div style={{ ...S.cell, fontWeight: 700, color: 'var(--svi-purple)' }}>{r.po_number}</div>
                                                    <div style={S.cell}>
                                                        {invoices.length === 0 ? <span style={{ color: 'var(--svi-text-faint)' }}>—</span> : invoices.map(n => (
                                                            <button key={n} className="svi-link" onClick={() => openPdf('invoice', n)}>{n}</button>
                                                        ))}
                                                    </div>
                                                    <div style={S.cell}>{r.party_name}</div>
                                                    <div style={{ ...S.cell, color: 'var(--svi-text-faint)' }}>{formatDate(r.po_date)}</div>
                                                    <div style={S.cell}>{r.sku_name}</div>
                                                    <div style={{ ...S.cell, ...S.numTab, color: r.ageing_days == null ? 'var(--svi-text-faint)' : 'var(--svi-text)' }}>
                                                        {r.ageing_days == null ? '—' : `${r.ageing_days}d`}
                                                    </div>
                                                    <div style={S.cell}>
                                                        <button className="svi-link" style={S.numTab} onClick={() => vouchers[0] && openPdf('so', vouchers[0])} title={vouchers.length > 1 ? `SOs: ${vouchers.join(', ')}` : undefined}>
                                                            {r.so_quantity}
                                                        </button>
                                                    </div>
                                                    <div style={S.cell}>
                                                        <button className="svi-link" style={S.numTab} onClick={() => invoices[0] && openPdf('invoice', invoices[0])} title={invoices.length > 1 ? `Invoices: ${invoices.join(', ')}` : undefined}>
                                                            {r.invoice_quantity}
                                                        </button>
                                                    </div>
                                                    <div style={S.cell}>
                                                        <div style={S.fillWrap}>
                                                            <div style={{ ...S.fillBar, width: `${Math.min(fillPct, 100)}%`, background: fillPct >= 100 ? 'var(--svi-green)' : fillPct >= 60 ? '#D8B570' : 'var(--svi-error)' }} />
                                                        </div>
                                                        <span style={{ fontSize: 11.5, color: 'var(--svi-text-faint)' }}>{fillPct}%</span>
                                                    </div>
                                                    <div style={{ ...S.cell, fontWeight: 700, color: r.difference === 0 ? 'var(--svi-text-faint)' : r.difference > 0 ? 'var(--svi-error)' : 'var(--svi-green)' }}>
                                                        {r.difference === 0 ? '0' : r.difference > 0 ? `-${r.difference}` : `+${-r.difference}`}
                                                    </div>
                                                    <div style={S.cell}>
                                                        <span style={{
                                                            ...S.statusBadge,
                                                            background: (STATUS_PILL[status] || STATUS_PILL.Matched).bg,
                                                            color: (STATUS_PILL[status] || STATUS_PILL.Matched).fg,
                                                        }}>{status}</span>
                                                    </div>
                                                </div>
                                            );
                                        })}
                                    </div>
                                </div>
                            </motion.div>
                        )}
                    </AnimatePresence>
                </>
            ) : (
                <motion.div key="summary" initial={{ opacity: 0, y: 8 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0 }} transition={{ duration: 0.18 }}>
                    <PoReport
                        report={poReport}
                        scopeLabel={scopeLabel}
                        rangeLabel={rangeLabel}
                        loss={{ total: summary.total_loss, short: summary.short_loss, cancel: summary.cancel_loss }}
                        loading={loading}
                        onJump={(tabKey) => { setTab(tabKey); setView('detail'); }}
                        onPickParty={(name) => { setTextSearch(name); }}
                    />
                    <div style={S.kpiRow}>
                        <KpiTile label="Total PO's Received" value={poReport.total} />
                        <KpiTile label="Total SO Qty" value={summary.total_so_qty} />
                        <KpiTile label="Total Invoice Qty" value={summary.total_invoice_qty} />
                        <KpiTile label="Short Supplied" value={summary.short_supplied} tone={summary.short_supplied > 0 ? 'error' : 'green'} />
                        <KpiTile label="Short-supply Loss" value={formatINR(summary.short_loss)} tone={summary.short_loss > 0 ? 'error' : 'green'} />
                        <KpiTile label="Cancellation Loss" value={formatINR(summary.cancel_loss)} tone={summary.cancel_loss > 0 ? 'error' : 'green'} />
                    </div>

                    <div style={S.summaryGrid}>
                        <div>
                            <div style={S.sectionHeaderRow}>
                                <h3 style={S.sectionTitle}>Top 10 key SKUs with highest potential loss</h3>
                                <span style={S.soInvRange}>{rangeLabel}</span>
                            </div>
                            <p style={S.lossHint}>Revenue at risk because the invoiced qty fell short of the SO qty (short qty × SO rate). Follows every filter above.</p>
                            {summary.top_loss_skus.length === 0 ? (
                                <p style={S.emptyState}>No short-supply loss in this range. 🎉</p>
                            ) : (
                                <div style={S.card}>
                                    <div style={S.scrollWrap}>
                                        <div style={{ minWidth: 820 }}>
                                            <div style={{ ...S.gridRow, ...S.gridHeader, gridTemplateColumns: '0.35fr 0.7fr 1.25fr 2.3fr 1.1fr 0.75fr 1fr' }}>
                                                <div style={S.headCell}>#</div>
                                                <div style={S.headCell}>Item Id</div>
                                                <div style={S.headCell}>UPC</div>
                                                <div style={S.headCell}>Item Name</div>
                                                <div style={S.headCell}>Description</div>
                                                <div style={{ ...S.headCell, textAlign: 'right' }}>Short Qty</div>
                                                <div style={{ ...S.headCell, textAlign: 'right' }}>Potential Loss</div>
                                            </div>
                                            {summary.top_loss_skus.map((s, idx) => (
                                                <div key={s.ean} className="svi-row" style={{ ...S.gridRow, gridTemplateColumns: '0.35fr 0.7fr 1.25fr 2.3fr 1.1fr 0.75fr 1fr' }}>
                                                    <div style={{ ...S.cell, color: 'var(--svi-text-faint)' }}>{idx + 1}</div>
                                                    <div style={{ ...S.cell, ...S.numTab }}>{s.item_id ?? '—'}</div>
                                                    <div style={{ ...S.cell, ...S.numTab }}>{s.ean}</div>
                                                    <div style={{ ...S.cell, whiteSpace: 'normal' }}>
                                                        {s.sku_name}
                                                        <div style={S.lossMeta}>Mostly at {s.top_party} ({s.top_party_share}%)</div>
                                                    </div>
                                                    <div style={{ ...S.cell, whiteSpace: 'normal' }}>{[s.pack_size, s.mrp != null ? `Rs ${Number(s.mrp).toFixed(1)}` : null].filter(Boolean).join(' - ') || '—'}</div>
                                                    <div style={{ ...S.cell, ...S.numTab, textAlign: 'right' }}>{s.short_supplied.toLocaleString()}</div>
                                                    <div style={{ ...S.cell, ...S.numTab, textAlign: 'right', fontWeight: 700, color: 'var(--svi-error)' }}>{formatINR(s.potential_loss)}</div>
                                                </div>
                                            ))}
                                            <div style={{ ...S.gridRow, gridTemplateColumns: '0.35fr 0.7fr 1.25fr 2.3fr 1.1fr 0.75fr 1fr', background: 'var(--svi-surface-subtle)', fontWeight: 700 }}>
                                                <div style={S.cell} />
                                                <div style={{ ...S.cell, gridColumn: 'span 4' }}>Top 10 total · {summary.total_loss > 0 ? Math.round((summary.top10_loss / summary.total_loss) * 100) : 0}% of all potential loss in this view</div>
                                                <div style={S.cell} />
                                                <div style={{ ...S.cell, ...S.numTab, textAlign: 'right', color: 'var(--svi-error)' }}>{formatINR(summary.top10_loss)}</div>
                                            </div>
                                        </div>
                                    </div>
                                </div>
                            )}
                        </div>

                        <div style={S.insightsCard}>
                            <div style={S.insightsHeaderRow}>
                                <span style={S.insightsKicker}>Key Insights</span>
                                <span style={S.soInvRange}>{rangeLabel}</span>
                            </div>
                            {summary.top_short_parties.length === 0 ? (
                                <p style={{ ...S.emptyState, padding: '32px 8px' }}>No short-supply issues in this range. 🎉</p>
                            ) : (
                                <>
                                    <div style={S.insightsHighlight}>
                                        <div style={S.insightsHighlightLabel}>Most Impacted Party</div>
                                        <div style={S.insightsHighlightName}>{summary.top_short_parties[0].party_name}</div>
                                        <div style={S.insightsHighlightValue}>{formatINR(summary.top_short_parties[0].potential_loss)} potential loss · {summary.top_short_parties[0].short_supplied.toLocaleString()} units short across {summary.top_short_parties[0].po_count} PO{summary.top_short_parties[0].po_count > 1 ? 's' : ''}</div>
                                    </div>
                                    <div style={S.insightsListTitle}>Parties Ranked by Potential Loss</div>
                                    <div style={S.insightsList}>
                                        {summary.top_short_parties.map((p, idx) => (
                                            <div key={p.party_name} style={S.insightsRow}>
                                                <span style={S.insightsRank}>{idx + 1}</span>
                                                <div style={{ flex: 1, minWidth: 0 }}>
                                                    <div style={S.insightsPartyName}>{p.party_name}</div>
                                                    <div style={S.insightsPartyMeta}>{p.po_count} PO{p.po_count > 1 ? 's' : ''} · SO {p.total_so_qty.toLocaleString()} · Inv {p.total_invoice_qty.toLocaleString()} · -{p.short_supplied.toLocaleString()} units</div>
                                                </div>
                                                <div style={S.insightsShortVal}>-{formatINR(p.potential_loss)}</div>
                                            </div>
                                        ))}
                                    </div>
                                </>
                            )}
                        </div>
                    </div>
                </motion.div>
            )}
        </div>
    );
}

const COLS = '0.4fr 1fr 1.2fr 1.3fr 0.9fr 1.8fr 0.7fr 0.8fr 0.9fr 1fr 0.9fr 0.9fr';

// Whole-rupee Indian grouping, e.g. 81614.02 -> "₹81,614".
// Management-level PO report: one plain-language conclusion, a status
// funnel (each tile jumps to the matching detail tab), a stacked bar, the
// party breakdown (click a party to filter to it), and the overdue POs.
function PoReport({ report, scopeLabel, rangeLabel, loss, loading, onJump, onPickParty }) {
    if (loading) return <div style={S.poReportCard}><p style={S.emptyState}>Loading PO report…</p></div>;
    const n = report.total;
    const pct = (k) => (n > 0 ? Math.round((report.by[k].length / n) * 100) : 0);
    const cnt = (k) => report.by[k].length;
    const fmt = (v) => Number(v || 0).toLocaleString('en-IN');

    const sentences = n === 0 ? ['No POs received for this selection.'] : [
        `${fmt(n)} PO${n !== 1 ? 's' : ''} received from ${scopeLabel} (${rangeLabel.toLowerCase()}) — ${fmt(report.totalQty)} units, ${formatINR(report.totalValue)} order value.`,
        cnt('Cancelled') > 0 ? `${cnt('Cancelled')} cancelled (${pct('Cancelled')}%) — ${fmt(report.cancelledQty)} units / ${formatINR(report.cancelledValue)} of orders lost.` : 'No POs cancelled.',
        `${cnt('Dispatched')} fully dispatched, ${cnt('Short')} dispatched short${report.fill !== null ? ` — overall fill rate ${report.fill}% on live POs` : ''}.`,
        cnt('Not Dispatched') > 0
            ? `${cnt('Not Dispatched')} not dispatched for more than ${DISPATCH_GRACE_DAYS} days${report.overdue[0] ? ` (oldest: ${report.overdue[0].po_number}, ${report.overdue[0].age} days)` : ''}${report.noSo > 0 ? ` — ${report.noSo} PO${report.noSo !== 1 ? 's' : ''} still without an SO` : ''}.`
            : `Nothing overdue — every live PO older than ${DISPATCH_GRACE_DAYS} days has been dispatched.`,
        cnt('Yet to Dispatch') > 0 ? `${cnt('Yet to Dispatch')} received in the last ${DISPATCH_GRACE_DAYS} days, still within normal dispatch time.` : null,
        loss.total > 0 ? `Potential loss ${formatINR(loss.total)}: ${formatINR(loss.short)} from short / not-dispatched supply, ${formatINR(loss.cancel)} from cancelled POs.` : null,
    ].filter(Boolean);

    return (
        <div style={S.poReportCard}>
            <div style={S.poReportHead}>
                <div>
                    <div style={S.poReportKicker}>PO Fulfilment Report</div>
                    <div style={S.poReportTitle}>{scopeLabel}</div>
                </div>
                <span style={S.soInvRange}>{rangeLabel}</span>
            </div>

            <ul style={S.poReportList}>
                {sentences.map((t, i) => <li key={i} style={{ marginBottom: 4 }}>{t}</li>)}
            </ul>

            {n > 0 && (
                <>
                    <div style={S.poBar}>
                        {PO_STATUSES.map(st => cnt(st.key) > 0 && (
                            <div key={st.key} title={`${st.label}: ${cnt(st.key)}`} style={{ width: `${(cnt(st.key) / n) * 100}%`, background: st.color }} />
                        ))}
                    </div>
                    <div style={S.poTiles}>
                        <div style={{ ...S.poTile, borderTopColor: '#231C30', cursor: 'default' }}>
                            <div style={{ ...S.poTileN, color: 'var(--svi-text)' }}>{fmt(n)}</div>
                            <div style={S.poTileL}>POs received</div>
                        </div>
                        {PO_STATUSES.map(st => (
                            <button key={st.key} className="svi-tab" style={{ ...S.poTile, borderTopColor: st.color }} onClick={() => onJump(st.tab)} title="Show these lines in the report">
                                <div style={{ ...S.poTileN, color: st.color }}>{cnt(st.key)} <span style={S.poTilePct}>{pct(st.key)}%</span></div>
                                <div style={S.poTileL}>{st.label}</div>
                            </button>
                        ))}
                    </div>
                </>
            )}

            {report.partyRows.length > 1 && (
                <>
                    <div style={S.poSubTitle}>By party <span style={{ fontWeight: 500, color: 'var(--svi-text-faint)' }}>— click a party to see only its report</span></div>
                    <div style={S.scrollWrap}>
                        <table style={S.poTable}>
                            <thead>
                                <tr>
                                    <th style={S.poTh}>Party</th>
                                    <th style={S.poThR}>POs</th>
                                    {PO_STATUSES.map(st => <th key={st.key} style={{ ...S.poThR, color: st.color }}>{st.key}</th>)}
                                    <th style={S.poThR}>Fill %</th>
                                </tr>
                            </thead>
                            <tbody>
                                {report.partyRows.slice(0, 15).map(p => (
                                    <tr key={p.party_name} className="svi-row" style={{ cursor: 'pointer' }} onClick={() => onPickParty(p.party_name)}>
                                        <td style={S.poTd}>{p.party_name}</td>
                                        <td style={S.poTdR}><b>{p.total}</b></td>
                                        {PO_STATUSES.map(st => <td key={st.key} style={{ ...S.poTdR, color: p.counts[st.key] ? st.color : 'var(--svi-text-faint)', fontWeight: p.counts[st.key] ? 700 : 400 }}>{p.counts[st.key] || '—'}</td>)}
                                        <td style={S.poTdR}>{p.fill === null ? '—' : `${p.fill}%`}</td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </div>
                    {report.partyRows.length > 15 && <p style={{ fontSize: 12, color: 'var(--svi-text-faint)', margin: '6px 0 0' }}>+ {report.partyRows.length - 15} more parties — search a party to narrow down.</p>}
                </>
            )}

            {report.overdue.length > 0 && (
                <>
                    <div style={S.poSubTitle}>Not dispatched for {DISPATCH_GRACE_DAYS}+ days <span style={{ fontWeight: 500, color: 'var(--svi-text-faint)' }}>— oldest first</span></div>
                    <div style={S.scrollWrap}>
                        <table style={S.poTable}>
                            <thead><tr><th style={S.poTh}>PO</th><th style={S.poTh}>Party</th><th style={S.poTh}>PO date</th><th style={S.poThR}>Age</th><th style={S.poThR}>Qty</th><th style={S.poTh}>SO</th></tr></thead>
                            <tbody>
                                {report.overdue.slice(0, 10).map(p => (
                                    <tr key={p.po_number} className="svi-row">
                                        <td style={{ ...S.poTd, fontWeight: 700 }}>{p.po_number}</td>
                                        <td style={S.poTd}>{p.party_name}</td>
                                        <td style={S.poTd}>{formatDate(p.po_date)}</td>
                                        <td style={{ ...S.poTdR, fontWeight: 700, color: 'var(--svi-error)' }}>{p.age}d</td>
                                        <td style={S.poTdR}>{Number(p.so_qty || p.po_units || 0).toLocaleString('en-IN')}</td>
                                        <td style={S.poTd}>{p.so_count > 0 ? 'Made' : <span style={{ color: 'var(--svi-error)', fontWeight: 700 }}>Not made</span>}</td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </div>
                    {report.overdue.length > 10 && <p style={{ fontSize: 12, color: 'var(--svi-text-faint)', margin: '6px 0 0' }}>+ {report.overdue.length - 10} more — open the “Not Dispatched” tab.</p>}
                </>
            )}
        </div>
    );
}

const NOT_IN_MASTER = 'Not in SKU Master';
const SKU_STATUS_COLORS = {
    'Active': '#16A34A',
    'Festive': '#D97706',
    'Discontinued': '#DC2626',
    'Packing Material': '#64748B',
    [NOT_IN_MASTER]: '#9A90AE',
};

function formatINR(v) {
    return `₹${Math.round(Number(v) || 0).toLocaleString('en-IN')}`;
}

function KpiTile({ label, value, tone }) {
    const color = tone === 'error' ? 'var(--svi-error)' : tone === 'green' ? 'var(--svi-green)' : 'var(--svi-text)';
    return (
        <div style={S.kpiTile}>
            <div style={{ fontSize: 26, fontWeight: 700, color }}>{value?.toLocaleString?.() ?? value ?? '—'}</div>
            <div style={S.kpiLabel}>{label}</div>
        </div>
    );
}

/* ---------------------------------------------------------------------- */
/* Styles — self-scoped CSS variables, matching GRN Management's visual   */
/* polish: purple accent, rounded white cards, dark header, soft shadows. */
/* ---------------------------------------------------------------------- */
const EMBEDDED_CSS = `
@import url('https://fonts.googleapis.com/css2?family=Poppins:wght@300;400;500;600;700&display=swap');
:root {
  --svi-purple: #6E58A6; --svi-purple-tint: #EEE9F8;
  --svi-surface: #fff; --svi-surface-subtle: #FBFAFD;
  --svi-border: #ECE7F2; --svi-border-soft: #E4DEEE;
  --svi-text: #231C30; --svi-text-faint: #9A90AE;
  --svi-error: #C25E77; --svi-error-tint: #F7E5EA;
  --svi-green: #5C7E60; --svi-green-tint: #E7F0E8;
  --svi-shadow-sm: 0 1px 2px rgba(35,28,48,0.05);
  --svi-shadow-md: 0 6px 20px rgba(35,28,48,0.10);
}
.svi-tab { font-family: 'Poppins', sans-serif; transition: transform 0.15s ease, background 0.2s ease; cursor: pointer; }
.svi-tab:active:not(:disabled) { transform: scale(0.96); }
.svi-row:nth-child(even) { background: var(--svi-surface-subtle); }
.svi-row:hover { background: var(--svi-purple-tint); }
.svi-input { font-family: 'Poppins', sans-serif; padding: 11px 18px; border-radius: 999px; border: 1px solid var(--svi-border-soft); background: #fff; color: var(--svi-text); font-size: 13.5px; }
.svi-input:focus { border-color: var(--svi-purple) !important; box-shadow: 0 0 0 3px rgba(110,88,166,0.18); outline: none; }
.svi-link { font-family: 'Poppins', sans-serif; background: none; border: none; padding: 0; margin-right: 6px; color: var(--svi-purple); font-weight: 600; cursor: pointer; text-decoration: underline; font-size: inherit; }
.svi-link:hover { color: #4F3C7D; }
* { scrollbar-width: thin; scrollbar-color: rgba(110,88,166,0.35) transparent; }
*::-webkit-scrollbar { width: 8px; height: 8px; }
*::-webkit-scrollbar-track { background: transparent; }
*::-webkit-scrollbar-thumb { background: rgba(110,88,166,0.35); border-radius: 999px; }
`;

const S = {
    page: { padding: 28, fontFamily: "'Poppins', sans-serif", color: 'var(--svi-text)' },
    headerCard: { display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: 16, flexWrap: 'wrap', background: 'linear-gradient(135deg, #1A1529 0%, #120D1D 100%)', border: '1px solid #2A2338', borderRadius: 16, padding: '28px 30px', marginBottom: 18, boxShadow: '0 8px 24px rgba(20,16,31,0.25)' },
    kicker: { fontSize: 12, fontWeight: 700, letterSpacing: '0.1em', textTransform: 'uppercase', color: '#A48FD1' },
    title: { fontSize: 29, fontWeight: 500, margin: '4px 0 8px', color: '#fff', letterSpacing: '-0.01em' },
    subtitle: { fontSize: 14.5, color: '#B2AAC4', margin: 0, maxWidth: 560, lineHeight: 1.5 },
    summaryBtn: { padding: '10px 18px', borderRadius: 10, border: 'none', background: '#fff', color: 'var(--svi-text)', fontWeight: 600, fontSize: 13 },
    downloadBtn: { padding: '10px 18px', borderRadius: 10, border: 'none', background: 'var(--svi-green)', color: '#fff', fontWeight: 600, fontSize: 13 },
    toast: { background: '#231C30', color: '#fff', padding: '10px 16px', borderRadius: 10, fontSize: 13, marginBottom: 14 },
    controlsCard: { background: 'var(--svi-surface)', border: '1px solid var(--svi-border)', borderRadius: 14, padding: 16, boxShadow: 'var(--svi-shadow-sm)', marginBottom: 18 },
    tabRow: { display: 'flex', flexWrap: 'wrap', gap: 8, alignItems: 'center', marginBottom: 14 },
    tabBtn: { display: 'flex', alignItems: 'center', gap: 8, padding: '9px 14px', borderRadius: 10, border: '1px solid var(--svi-border-soft)', background: '#fff', color: 'var(--svi-text)', fontWeight: 600, fontSize: 13 },
    tabBtnActive: { background: '#1B1626', color: '#fff', borderColor: '#1B1626' },
    tabCount: { background: 'var(--svi-surface-subtle)', color: 'var(--svi-text-faint)', borderRadius: 999, padding: '2px 8px', fontSize: 11.5, fontWeight: 700 },
    tabCountActive: { background: 'rgba(255,255,255,0.18)', color: '#fff' },
    quickBtn: { padding: '9px 14px', borderRadius: 10, border: '1px solid var(--svi-border-soft)', background: '#fff', color: 'var(--svi-text-faint)', fontWeight: 500, fontSize: 12.5 },
    quickBtnActive: { borderColor: 'var(--svi-purple)', color: 'var(--svi-purple)', fontWeight: 700 },
    searchRow: { display: 'flex', flexWrap: 'wrap', gap: 10, alignItems: 'center' },
    showingText: { fontSize: 13, color: 'var(--svi-text-faint)', margin: '14px 0 0' },
    emptyState: { padding: 48, textAlign: 'center', color: 'var(--svi-text-faint)', fontSize: 14 },
    card: { background: 'var(--svi-surface)', border: '1px solid var(--svi-border)', borderRadius: 16, overflow: 'hidden', boxShadow: 'var(--svi-shadow-md)' },
    scrollWrap: { overflow: 'auto', maxHeight: '65vh' },
    gridRow: { display: 'grid', alignItems: 'center', borderBottom: '1px solid var(--svi-border)' },
    gridHeader: { position: 'sticky', top: 0, background: '#231C30', color: '#fff', fontSize: 11, fontWeight: 600, letterSpacing: '0.04em', textTransform: 'uppercase', zIndex: 1 },
    cell: { padding: '13px 14px', fontSize: 13.5, overflow: 'hidden' },
    headCell: { padding: '13px 14px', overflow: 'hidden', whiteSpace: 'nowrap', textOverflow: 'ellipsis' },
    numTab: { fontVariantNumeric: 'tabular-nums' },
    fillWrap: { background: 'var(--svi-border)', borderRadius: 999, height: 6, width: '100%', marginBottom: 4, overflow: 'hidden' },
    fillBar: { height: '100%', borderRadius: 999 },
    statusBadge: { padding: '4px 10px', borderRadius: 999, fontSize: 11.5, fontWeight: 700 },
    soInvCard: { background: 'var(--svi-surface)', border: '1px solid var(--svi-border)', borderRadius: 14, padding: '18px 22px', boxShadow: 'var(--svi-shadow-sm)', marginBottom: 18 },
    soInvHeader: { display: 'flex', justifyContent: 'space-between', alignItems: 'baseline', marginBottom: 14 },
    soInvTitle: { fontSize: 14, fontWeight: 700, color: 'var(--svi-text)' },
    soInvRange: { fontSize: 12, fontWeight: 600, color: 'var(--svi-purple)', background: 'var(--svi-purple-tint)', padding: '3px 10px', borderRadius: 999 },
    soInvBody: { display: 'flex', alignItems: 'center', gap: 24, flexWrap: 'wrap' },
    soInvStat: { minWidth: 120 },
    soInvNum: { fontSize: 30, fontWeight: 700, color: 'var(--svi-text)', fontVariantNumeric: 'tabular-nums', lineHeight: 1.1 },
    soInvLabel: { fontSize: 11.5, color: 'var(--svi-text-faint)', textTransform: 'uppercase', letterSpacing: '0.04em', fontWeight: 600, marginTop: 4 },
    soInvVs: { fontSize: 13, fontWeight: 700, color: 'var(--svi-text-faint)' },
    soInvDivider: { width: 1, alignSelf: 'stretch', background: 'var(--svi-border)' },
    soInvBarWrap: { flex: 1, minWidth: 180 },
    soInvBarTrack: { background: 'var(--svi-border)', borderRadius: 999, height: 10, width: '100%', overflow: 'hidden', marginBottom: 6 },
    soInvBarFill: { height: '100%', borderRadius: 999, background: 'linear-gradient(90deg, var(--svi-purple), #8E78C6)' },
    soInvPctText: { fontSize: 12, color: 'var(--svi-text-faint)', fontWeight: 600 },
    kpiRow: { display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(170px, 1fr))', gap: 14, marginBottom: 28 },
    poReportCard: { background: 'var(--svi-surface)', border: '1px solid var(--svi-border)', borderRadius: 16, padding: '20px 22px', boxShadow: 'var(--svi-shadow-sm)', marginBottom: 22 },
    poReportHead: { display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: 12, marginBottom: 10 },
    poReportKicker: { fontSize: 11, fontWeight: 700, letterSpacing: '0.1em', textTransform: 'uppercase', color: 'var(--svi-purple)' },
    poReportTitle: { fontSize: 19, fontWeight: 700, color: 'var(--svi-text)', marginTop: 2 },
    poReportList: { margin: '0 0 14px', paddingLeft: 18, fontSize: 14, lineHeight: 1.6, color: 'var(--svi-text)' },
    poBar: { display: 'flex', height: 12, borderRadius: 999, overflow: 'hidden', background: 'var(--svi-surface-subtle)', marginBottom: 14 },
    poTiles: { display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(140px, 1fr))', gap: 10 },
    poTile: { textAlign: 'left', background: 'var(--svi-surface-subtle)', border: '1px solid var(--svi-border)', borderTop: '4px solid', borderRadius: 12, padding: '12px 14px', fontFamily: 'inherit' },
    poTileN: { fontSize: 24, fontWeight: 800, lineHeight: 1.1 },
    poTilePct: { fontSize: 12, fontWeight: 600, opacity: 0.75 },
    poTileL: { fontSize: 11.5, fontWeight: 600, color: 'var(--svi-text-faint)', marginTop: 4 },
    poSubTitle: { fontSize: 13.5, fontWeight: 700, color: 'var(--svi-text)', margin: '20px 0 8px' },
    poTable: { width: '100%', borderCollapse: 'collapse', fontSize: 13 },
    poTh: { textAlign: 'left', padding: '8px 10px', fontSize: 11, textTransform: 'uppercase', letterSpacing: '0.04em', color: 'var(--svi-text-faint)', borderBottom: '1px solid var(--svi-border)', whiteSpace: 'nowrap' },
    poThR: { textAlign: 'right', padding: '8px 10px', fontSize: 11, textTransform: 'uppercase', letterSpacing: '0.04em', color: 'var(--svi-text-faint)', borderBottom: '1px solid var(--svi-border)', whiteSpace: 'nowrap' },
    poTd: { padding: '8px 10px', borderBottom: '1px solid var(--svi-border)', color: 'var(--svi-text)' },
    poTdR: { padding: '8px 10px', borderBottom: '1px solid var(--svi-border)', color: 'var(--svi-text)', textAlign: 'right', fontVariantNumeric: 'tabular-nums' },
    skuFilterRow: { display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap', marginTop: 12 },
    skuFilterLabel: { fontSize: 11, fontWeight: 700, letterSpacing: '0.08em', textTransform: 'uppercase', color: 'var(--svi-text-faint)', marginRight: 4 },
    skuChip: { padding: '7px 13px', borderRadius: 999, border: '1.5px solid var(--svi-border)', background: 'var(--svi-surface)', color: 'var(--svi-text)', fontSize: 12.5, fontWeight: 700 },
    skuChipActive: { background: 'var(--svi-purple)', borderColor: 'var(--svi-purple)', color: '#fff' },
    skuClear: { padding: '7px 12px', borderRadius: 8, border: '1px solid var(--svi-border)', background: 'transparent', color: 'var(--svi-error)', fontSize: 12.5, fontWeight: 700 },
    lossHint: { fontSize: 12, color: 'var(--svi-text-faint)', fontStyle: 'italic', margin: '-6px 0 12px' },
    lossMeta: { fontSize: 11, color: 'var(--svi-text-faint)', marginTop: 3 },
    kpiTile: { background: 'var(--svi-surface)', border: '1px solid var(--svi-border)', borderRadius: 14, padding: '18px 20px', boxShadow: 'var(--svi-shadow-sm)' },
    kpiLabel: { fontSize: 11, color: 'var(--svi-text-faint)', textTransform: 'uppercase', letterSpacing: '0.04em', marginTop: 6, fontWeight: 600 },
    sectionTitle: { fontSize: 15, fontWeight: 700, margin: 0 },
    sectionHeaderRow: { display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 12 },
    summaryGrid: { display: 'grid', gridTemplateColumns: 'minmax(0, 2fr) minmax(280px, 1fr)', gap: 20, alignItems: 'start' },
    insightsCard: { background: 'linear-gradient(165deg, #1A1529 0%, #120D1D 100%)', border: '1px solid #2A2338', borderRadius: 16, padding: '20px 22px', boxShadow: '0 8px 24px rgba(20,16,31,0.18)' },
    insightsHeaderRow: { display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 16 },
    insightsKicker: { fontSize: 13, fontWeight: 700, color: '#fff' },
    insightsHighlight: { background: 'rgba(255,255,255,0.06)', border: '1px solid rgba(255,255,255,0.1)', borderRadius: 12, padding: '14px 16px', marginBottom: 18 },
    insightsHighlightLabel: { fontSize: 10.5, color: '#A48FD1', textTransform: 'uppercase', letterSpacing: '0.06em', fontWeight: 700, marginBottom: 6 },
    insightsHighlightName: { fontSize: 16, fontWeight: 700, color: '#fff', marginBottom: 4 },
    insightsHighlightValue: { fontSize: 12.5, color: '#C9BFE0' },
    insightsListTitle: { fontSize: 10.5, color: '#8E84A8', textTransform: 'uppercase', letterSpacing: '0.06em', fontWeight: 700, marginBottom: 10 },
    insightsList: { display: 'flex', flexDirection: 'column', gap: 2 },
    insightsRow: { display: 'flex', alignItems: 'center', gap: 10, padding: '9px 0', borderBottom: '1px solid rgba(255,255,255,0.07)' },
    insightsRank: { width: 20, height: 20, borderRadius: '50%', background: 'rgba(255,255,255,0.1)', color: '#C9BFE0', fontSize: 11, fontWeight: 700, display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 },
    insightsPartyName: { fontSize: 13, fontWeight: 600, color: '#fff', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' },
    insightsPartyMeta: { fontSize: 11, color: '#8E84A8', marginTop: 2 },
    insightsShortVal: { fontSize: 13, fontWeight: 700, color: '#E08B9F', flexShrink: 0, fontVariantNumeric: 'tabular-nums' },
};
